<template>
  <Card class="layout-card platform-query-page">
    <div class="page-scroll">
      <div class="page-header">
        <div>
          <h2>支付平台查询</h2>
          <p>直接核对三方平台数据，查询操作不会修改本地订单或订阅状态</p>
        </div>
      </div>

      <Tabs v-model="activeTab" :animated="false" class="platform-query-tabs">
        <TabPane label="订单查询" name="order">
          <div class="query-band">
            <Select
              v-model="orderForm.payPlatform"
              placeholder="选择支付平台"
              class="platform-select"
            >
              <Option
                v-for="item in payPlatformOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </Option>
            </Select>
            <Input
              v-model.trim="orderForm.reference"
              clearable
              :placeholder="orderReferencePlaceholder"
              @on-enter="handleOrderQuery"
            >
              <Icon slot="prefix" type="md-search" />
            </Input>
            <Button
              type="primary"
              icon="md-search"
              :loading="orderLoading"
              @click="handleOrderQuery"
            >
              查询平台订单
            </Button>
            <Button icon="md-trash" @click="clearOrderResult">清空</Button>
          </div>

          <div v-if="orderSearched" class="result-area">
            <Alert :type="orderAlertType" show-icon>
              {{ orderResult.message || orderConclusion }}
            </Alert>
            <Alert
              v-if="orderResult.found"
              :type="orderResult.localOrderMatched ? 'success' : 'warning'"
              show-icon
            >
              {{ orderResult.localMatchMessage || '尚未执行本地订单关联' }}
            </Alert>
            <section class="result-section">
              <h3>订单摘要</h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <span>支付平台</span>
                  <strong>{{ platformName(orderResult.payPlatform) }}</strong>
                </div>
                <div class="detail-item">
                  <span>查询标识类型</span>
                  <strong>{{ displayValue(orderResult.lookupType) }}</strong>
                </div>
                <div class="detail-item detail-wide">
                  <span>查询标识</span>
                  <CopyableText :value="orderResult.reference" code />
                </div>
                <div class="detail-item">
                  <span>平台状态</span>
                  <Tag :color="orderResult.found ? 'success' : 'default'">
                    {{ displayValue(orderResult.status) }}
                  </Tag>
                </div>
                <div class="detail-item">
                  <span>运行环境</span>
                  <Tag :color="orderResult.sandbox ? 'warning' : 'success'">
                    {{ orderResult.sandbox ? '测试环境' : '正式环境' }}
                  </Tag>
                </div>
                <div class="detail-item">
                  <span>平台支付ID</span>
                  <CopyableText :value="orderResult.paymentId" code />
                </div>
                <div class="detail-item">
                  <span>支付请求ID</span>
                  <CopyableText :value="orderResult.paymentRequestId" code />
                </div>
                <div class="detail-item">
                  <span>订阅ID</span>
                  <CopyableText :value="orderResult.subscriptionId" code />
                </div>
                <div class="detail-item">
                  <span>购买令牌</span>
                  <CopyableText :value="orderResult.purchaseToken" code />
                </div>
                <div class="detail-item">
                  <span>关联订单ID</span>
                  <CopyableText :value="orderResult.internalOrderId" code />
                </div>
                <div class="detail-item">
                  <span>关联用户ID</span>
                  <CopyableText :value="orderResult.userId" code />
                </div>
                <div class="detail-item detail-wide">
                  <span>关联订单号</span>
                  <CopyableText :value="orderResult.localOrderNo" code />
                </div>
                <div class="detail-item">
                  <span>平台商品ID</span>
                  <CopyableText :value="orderResult.productId" code />
                </div>
                <div class="detail-item">
                  <span>商品名称</span>
                  <strong>{{ displayValue(orderResult.productName) }}</strong>
                </div>
                <div class="detail-item">
                  <span>支付金额</span>
                  <strong>{{ orderAmount }}</strong>
                </div>
                <div class="detail-item">
                  <span>税额</span>
                  <strong>{{ taxAmount }}</strong>
                </div>
                <div class="detail-item">
                  <span>创建时间</span>
                  <strong>{{ formatDateTime(orderResult.createTime) }}</strong>
                </div>
                <div class="detail-item">
                  <span>支付时间</span>
                  <strong>{{ formatDateTime(orderResult.payTime) }}</strong>
                </div>
              </div>
            </section>
            <RawJsonPanel :value="orderResult.rawJson" />
          </div>
        </TabPane>

        <TabPane label="订阅查询" name="subscription">
          <div class="query-band">
            <Select
              v-model="subscriptionForm.payPlatform"
              placeholder="选择支付平台"
              class="platform-select"
            >
              <Option
                v-for="item in subscriptionPlatformOptions"
                :key="item.value"
                :value="item.value"
              >
                {{ item.label }}
              </Option>
            </Select>
            <Input
              v-model.trim="subscriptionForm.subscriptionId"
              clearable
              placeholder="输入平台订阅ID，例如 sub_xxx"
              @on-enter="handleSubscriptionQuery"
            >
              <Icon slot="prefix" type="md-repeat" />
            </Input>
            <Button
              type="primary"
              icon="md-sync"
              :loading="subscriptionLoading"
              @click="handleSubscriptionQuery"
            >
              查询订阅状态
            </Button>
            <Button icon="md-trash" @click="clearSubscriptionResult">
              清空
            </Button>
          </div>

          <div v-if="subscriptionSearched" class="result-area">
            <Alert :type="subscriptionAlertType" show-icon>
              {{ subscriptionResult.message || '暂无查询结论' }}
            </Alert>
            <section class="result-section">
              <h3>订阅摘要</h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <span>支付平台</span>
                  <strong>{{ subscriptionResult.payPlatformName }}</strong>
                </div>
                <div class="detail-item detail-wide">
                  <span>订阅ID</span>
                  <CopyableText :value="subscriptionResult.subscriptionId" code />
                </div>
                <div class="detail-item">
                  <span>平台状态</span>
                  <Tag :color="subscriptionStatusColor">
                    {{ displayValue(subscriptionResult.platformStatusName) }}
                  </Tag>
                </div>
                <div class="detail-item">
                  <span>平台原始状态</span>
                  <strong>{{ displayValue(subscriptionResult.platformStatus) }}</strong>
                </div>
                <div class="detail-item">
                  <span>平台是否存在</span>
                  <strong>{{ formatBoolean(subscriptionResult.subscriptionExists) }}</strong>
                </div>
                <div class="detail-item">
                  <span>是否自动续订</span>
                  <strong>{{ formatBoolean(subscriptionResult.autoRenew) }}</strong>
                </div>
                <div class="detail-item">
                  <span>订阅开启时间</span>
                  <strong>{{ formatDateTime(subscriptionResult.subscriptionStartTime) }}</strong>
                </div>
                <div class="detail-item">
                  <span>平台取消时间</span>
                  <strong>{{ formatDateTime(subscriptionResult.cancellationTime) }}</strong>
                </div>
                <div class="detail-item">
                  <span>当前周期开始</span>
                  <strong>{{ formatDateTime(subscriptionResult.currentPeriodStart) }}</strong>
                </div>
                <div class="detail-item">
                  <span>当前周期结束</span>
                  <strong>{{ formatDateTime(subscriptionResult.currentPeriodEnd) }}</strong>
                </div>
                <div class="detail-item">
                  <span>状态更新时间</span>
                  <strong>{{ formatDateTime(subscriptionResult.statusUpdateTime) }}</strong>
                </div>
              </div>
            </section>
            <RawJsonPanel :value="subscriptionResult.rawJson" />
          </div>
        </TabPane>
      </Tabs>
    </div>
  </Card>
</template>

<script>
import CopyableText from '../components/copyable-text.vue'
import RawJsonPanel from './raw-json-panel.vue'
import {
  queryPlatformOrder,
  queryPlatformSubscription
} from '@/api/customer-service'
import {
  PAY_PLATFORM_OPTIONS,
  displayValue,
  formatDateTime,
  getPayPlatformLabel
} from '../constants'

export default {
  name: 'CustomerServicePlatformQuery',
  components: { CopyableText, RawJsonPanel },
  data() {
    return {
      activeTab: 'order',
      payPlatformOptions: PAY_PLATFORM_OPTIONS,
      subscriptionPlatformOptions: PAY_PLATFORM_OPTIONS.filter((item) =>
        [3, 4, 5].includes(item.value)
      ),
      orderForm: { payPlatform: null, reference: '' },
      subscriptionForm: { payPlatform: null, subscriptionId: '' },
      orderLoading: false,
      subscriptionLoading: false,
      orderSearched: false,
      subscriptionSearched: false,
      orderResult: {},
      subscriptionResult: {}
    }
  },
  computed: {
    orderReferencePlaceholder() {
      const placeholders = {
        1: '输入GPA订单号',
        2: '输入Apple交易ID',
        3: '输入Antom paymentId',
        4: '输入Stripe pi_xxx',
        5: '输入PayPal Order、Capture或Sale ID'
      }
      return placeholders[this.orderForm.payPlatform] || '输入平台订单标识'
    },
    orderAlertType() {
      if (this.orderResult.supported === false) return 'warning'
      return this.orderResult.found ? 'success' : 'warning'
    },
    orderConclusion() {
      return this.orderResult.found
        ? '支付平台已查到该订单'
        : '支付平台未查到对应订单'
    },
    orderAmount() {
      return this.formatMoney(
        this.orderResult.amountValue,
        this.orderResult.currency
      )
    },
    taxAmount() {
      return this.formatMoney(
        this.orderResult.taxAmount,
        this.orderResult.currency
      )
    },
    subscriptionAlertType() {
      if (!this.subscriptionResult.querySuccess) return 'error'
      return this.subscriptionResult.subscriptionExists === false
        ? 'warning'
        : 'success'
    },
    subscriptionStatusColor() {
      if (!this.subscriptionResult.querySuccess) return 'error'
      if (this.subscriptionResult.subscriptionExists === false) return 'default'
      return this.subscriptionResult.autoRenew === false ? 'warning' : 'success'
    }
  },
  methods: {
    displayValue,
    formatDateTime,
    platformName(value) {
      return getPayPlatformLabel(value)
    },
    formatBoolean(value) {
      if (value === true) return '是'
      if (value === false) return '否'
      return '-'
    },
    formatMoney(value, currency) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      return Number.isNaN(number)
        ? `${currency || ''} ${value}`.trim()
        : `${currency || ''} ${number.toFixed(2)}`.trim()
    },
    validateForm(payPlatform, reference) {
      if (!payPlatform) {
        this.$Message.warning('请先选择支付平台')
        return false
      }
      if (!reference) {
        this.$Message.warning('请输入查询标识')
        return false
      }
      return true
    },
    async handleOrderQuery() {
      if (!this.validateForm(this.orderForm.payPlatform, this.orderForm.reference)) {
        return
      }
      this.orderLoading = true
      try {
        const { data } = await queryPlatformOrder(this.orderForm)
        this.orderResult = data || {}
        this.orderSearched = true
      } finally {
        this.orderLoading = false
      }
    },
    async handleSubscriptionQuery() {
      if (
        !this.validateForm(
          this.subscriptionForm.payPlatform,
          this.subscriptionForm.subscriptionId
        )
      ) {
        return
      }
      this.subscriptionLoading = true
      try {
        const { data } = await queryPlatformSubscription(this.subscriptionForm)
        this.subscriptionResult = data || {}
        this.subscriptionSearched = true
      } finally {
        this.subscriptionLoading = false
      }
    },
    clearOrderResult() {
      this.orderForm = { payPlatform: null, reference: '' }
      this.orderResult = {}
      this.orderSearched = false
    },
    clearSubscriptionResult() {
      this.subscriptionForm = { payPlatform: null, subscriptionId: '' }
      this.subscriptionResult = {}
      this.subscriptionSearched = false
    }
  }
}
</script>

<style scoped lang="less">
.platform-query-page {
  height: 100%;
  overflow: hidden;

  /deep/ .ivu-card-body {
    height: 100%;
    padding: 0;
  }
}

.page-scroll {
  display: flex;
  height: 100%;
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  padding: 24px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;

  h2 {
    margin: 0 0 5px;
    font-size: 20px;
  }

  p {
    margin: 0;
    color: #808695;
  }
}

.platform-query-tabs {
  display: flex;
  min-height: 0;
  flex: 1;
  flex-direction: column;

  /deep/ .ivu-tabs-bar {
    flex: 0 0 auto;
  }

  /deep/ .ivu-tabs-content {
    height: auto;
    min-height: 0;
    flex: 1;
  }

  /deep/ .ivu-tabs-tabpane {
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
}

.query-band {
  display: grid;
  grid-template-columns: 190px minmax(320px, 1fr) auto auto;
  gap: 10px;
  align-items: center;
  padding: 16px;
  border: 1px solid #e8eaec;
  background: #f8f9fb;
}

.result-area {
  margin-top: 16px;
}

.result-section {
  margin-top: 14px;

  h3 {
    margin: 0 0 12px;
    font-size: 15px;
  }
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  border-top: 1px solid #e8eaec;
  border-left: 1px solid #e8eaec;
}

.detail-item {
  min-width: 0;
  min-height: 70px;
  padding: 11px 13px;
  border-right: 1px solid #e8eaec;
  border-bottom: 1px solid #e8eaec;

  > span:first-child {
    display: block;
    margin-bottom: 7px;
    color: #808695;
    font-size: 12px;
  }

  > strong {
    display: block;
    overflow-wrap: anywhere;
    color: #17233d;
    font-weight: 500;
  }
}

.detail-wide {
  grid-column: span 2;
}

@media (max-width: 1100px) {
  .query-band {
    grid-template-columns: 180px minmax(240px, 1fr) auto;
  }

  .query-band > button:last-child {
    grid-column: 3;
  }

  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .platform-query-page {
    height: auto;

    /deep/ .ivu-card-body {
      height: auto;
    }
  }

  .page-scroll {
    display: block;
    overflow: visible;
  }

  .platform-query-tabs /deep/ .ivu-tabs-tabpane {
    height: auto;
    overflow: visible;
  }

  .query-band,
  .detail-grid {
    grid-template-columns: 1fr;
  }

  .query-band > button:last-child,
  .detail-wide {
    grid-column: auto;
  }
}
</style>
